import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import {
  INV_CATEGORIES,
  INV_DELIVERY_STATUSES,
  INV_SUPPLIERS,
  invCategoryLabel,
  invDeliveryStatusLabel,
} from "../lib/inventory";

export function NewInventoryItemPage() {
  const navigate = useNavigate();

  const [buildingId, setBuildingId] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [quantity, setQuantity] = useState("0");
  const [unitOfMeasure, setUnitOfMeasure] = useState("");
  const [reorderThreshold, setReorderThreshold] = useState("");
  const [deliveryStatus, setDeliveryStatus] = useState("in_stock");
  const [supplier, setSupplier] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const buildingsQ = useQuery({
    queryKey: ["ref", "buildings"],
    queryFn: () => api.reference.buildings(),
  });

  const createMut = useMutation({
    mutationFn: () =>
      api.inventory.create({
        buildingId: Number(buildingId),
        name: name.trim(),
        category: category || null,
        quantity: quantity === "" ? 0 : Number(quantity),
        unitOfMeasure: unitOfMeasure.trim() || null,
        reorderThreshold: reorderThreshold === "" ? null : Number(reorderThreshold),
        deliveryStatus,
        supplier: supplier || null,
        notes: notes.trim() || null,
      }),
    onSuccess: ({ id }) => navigate(`/inventory/${id}`),
    onError: () => setError("Could not save the item. Please try again."),
  });

  function onSave() {
    setError(null);
    if (!buildingId) return setError("Please choose a building.");
    if (!name.trim()) return setError("Please enter an item name.");
    createMut.mutate();
  }

  return (
    <Layout title="Add an Inventory Item">
      <p className="help-line">
        Pick the building, then name the item. Quantity and reorder level are optional but power
        the low-stock flag.
      </p>
      <section className="card form-card">
        <label className="field">
          <span>Building</span>
          <select value={buildingId} onChange={(e) => setBuildingId(e.target.value)}>
            <option value="">Choose a building…</option>
            {(buildingsQ.data?.buildings ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Item name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. All-purpose cleaner"
          />
        </label>

        <label className="field">
          <span>Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Not set</option>
            {INV_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {invCategoryLabel(cat)}
              </option>
            ))}
          </select>
        </label>

        <div className="two-col">
          <label className="field">
            <span>Quantity on hand</span>
            <input
              type="number"
              min="0"
              step="any"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Unit of measure</span>
            <input
              value={unitOfMeasure}
              onChange={(e) => setUnitOfMeasure(e.target.value)}
              placeholder="e.g. boxes, bottles"
            />
          </label>
        </div>

        <label className="field">
          <span>Reorder at (low-stock level)</span>
          <input
            type="number"
            min="0"
            step="any"
            value={reorderThreshold}
            onChange={(e) => setReorderThreshold(e.target.value)}
            placeholder="Leave blank for no alert"
          />
        </label>

        <div className="two-col">
          <label className="field">
            <span>Ordered status</span>
            <select value={deliveryStatus} onChange={(e) => setDeliveryStatus(e.target.value)}>
              {INV_DELIVERY_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {invDeliveryStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="field">
            <span>Supplier</span>
            <div className="choice-set">
              {INV_SUPPLIERS.map((s) => (
                <label key={s} className={"choice" + (supplier === s ? " selected" : "")}>
                  <input
                    type="radio"
                    name="supplier"
                    checked={supplier === s}
                    onChange={() => setSupplier(s)}
                  />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <label className="field">
          <span>Notes</span>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        {error && <p className="field-error">{error}</p>}

        <div className="form-actions">
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => navigate("/inventory")}
            disabled={createMut.isPending}
          >
            Cancel
          </button>
          <button className="btn btn-primary btn-xl" onClick={onSave} disabled={createMut.isPending}>
            {createMut.isPending ? "Saving…" : "Save item"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
